/**
 * GIWAHS — Supplier Portal pages
 * --------------------------------------------------------------------------
 * Five routes mounted under /supplier/*:
 *   /supplier/claim      — request ownership of an existing listing
 *   /supplier/dashboard  — overview + tier + metrics + inbox preview
 *   /supplier/inbox      — full list of assigned RFQs
 *   /supplier/inbox/:ref — RFQ detail + quote form
 *   /supplier/profile    — edit own supplier row (whitelisted fields)
 *
 * All RPCs are SECURITY DEFINER; supplier isolation is enforced server-side.
 */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Loader2, ArrowRight, AlertCircle, Check, Award, ChevronLeft, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import SEO from '../components/SEO';
import { supplierClient } from '../lib/supplierClient';
import { useAuth } from '../context/AuthContext';
import { track, EVENTS } from '../lib/analytics';

// ── Helpers ────────────────────────────────────────────────────────────────
const TIER_META = {
  verified: { label: 'GIWAHS Verified™', color: 'border-neutral-700 bg-neutral-100 text-neutral-900', tone: 'baseline credibility' },
  gold:     { label: 'GIWAHS Gold™',     color: 'border-amber-500 bg-amber-50 text-amber-900',         tone: 'priority placement + audit verified' },
  elite:    { label: 'GIWAHS Elite™',    color: 'border-black bg-black text-white',                    tone: 'top-tier curated partner' },
};

const TierBadge = ({ tier }) => {
  const m = TIER_META[tier] || TIER_META.verified;
  return (
    <span data-testid={`supplier-tier-${tier}`} className={`inline-flex items-center gap-1.5 border ${m.color} px-2.5 py-1 font-mono-data text-[10px] uppercase tracking-wider`}>
      <Award size={11} strokeWidth={1.5} /> {m.label}
    </span>
  );
};

const Metric = ({ label, value, sublabel }) => (
  <div className="border border-black bg-white p-4">
    <div className="label-overline text-neutral-500">{label}</div>
    <div className="mt-2 font-display text-2xl tracking-tight md:text-3xl">{value ?? '—'}</div>
    {sublabel && <div className="mt-1 font-mono-data text-[10px] uppercase tracking-wider text-neutral-500">{sublabel}</div>}
  </div>
);

// ── 1. Claim listing ──────────────────────────────────────────────────────
export const SupplierClaim = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [slug, setSlug] = useState('');
  const [email, setEmail] = useState(user?.email || '');
  const [role, setRole] = useState('');
  const [proof, setProof] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => { if (user?.email && !email) setEmail(user.email); }, [user, email]);
  useEffect(() => {
    if (!authLoading && !user) navigate('/login', { state: { from: '/supplier/claim' } });
  }, [authLoading, user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await supplierClient.claimListing({ supplier_slug: slug.trim(), contact_email: email, contact_role: role, proof_url: proof, reason });
      setSubmitted(true);
      toast.success('Claim submitted — the GIWAHS team will review within one business day.');
    } catch (err) {
      toast.error(err?.message || 'Could not submit claim.');
    } finally { setBusy(false); }
  };

  if (submitted) {
    return (
      <div data-testid="supplier-claim-success" className="bg-white">
        <div className="mx-auto max-w-2xl px-5 py-20 text-center md:py-28">
          <div className="mx-auto flex h-16 w-16 items-center justify-center border-2 border-black"><Check size={28} strokeWidth={1.5} /></div>
          <span className="mt-8 inline-block label-overline">Claim filed</span>
          <h1 className="mt-4 font-display tracking-tighter" style={{ fontWeight: 700, fontSize: 'clamp(2rem,5vw,3.5rem)', lineHeight: 0.95 }}>We'll be in touch.</h1>
          <p className="mt-5 text-neutral-700">Your request to manage <span className="font-mono-data text-black">{slug}</span> is in review. Once approved you'll get access to the supplier dashboard, RFQ inbox, and profile editor.</p>
          <Link to="/factories" className="mt-8 inline-block btn-giwahs btn-giwahs-secondary">Back to the directory</Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="supplier-claim-page" className="bg-white">
      <SEO title="Claim your factory listing — Supplier Portal" description="Take ownership of your GIWAHS factory listing." noindex />
      <section className="border-b border-black">
        <div className="mx-auto max-w-3xl px-5 py-14 md:py-20">
          <span className="label-overline text-neutral-500">/ Supplier · Claim listing</span>
          <h1 className="mt-3 font-display tracking-tighter" style={{ fontWeight: 700, fontSize: 'clamp(2rem,5vw,3.75rem)', lineHeight: 0.95 }}>Claim your factory.</h1>
          <p className="mt-5 max-w-xl text-neutral-700">If your factory is already on GIWAHS, claim it to receive RFQs directly, submit quotes, manage your profile, and progress through GIWAHS Verified → Gold → Elite tiers.</p>

          <form onSubmit={submit} className="mt-10 space-y-5 border border-black p-6 md:p-8">
            <div>
              <label className="label-overline">Your factory's GIWAHS slug</label>
              <input data-testid="supplier-claim-slug" required value={slug} onChange={(e) => setSlug(e.target.value)}
                placeholder="e.g. shenzhen-vector-mfg"
                className="mt-2 w-full border border-black bg-transparent px-3 py-3 font-mono-data text-sm outline-none placeholder:text-neutral-400" />
              <p className="mt-1 text-xs text-neutral-500">Find this in the URL of your factory page on giwahs.com/factory/&lt;slug&gt;.</p>
            </div>
            <div>
              <label className="label-overline">Work email</label>
              <input data-testid="supplier-claim-email" required type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                className="mt-2 w-full border border-black bg-transparent px-3 py-3 text-sm outline-none" />
            </div>
            <div>
              <label className="label-overline">Your role</label>
              <input data-testid="supplier-claim-role" value={role} onChange={(e) => setRole(e.target.value)}
                placeholder="e.g. Sales Director" className="mt-2 w-full border border-black bg-transparent px-3 py-3 text-sm outline-none" />
            </div>
            <div>
              <label className="label-overline">Proof of association (optional)</label>
              <input data-testid="supplier-claim-proof" value={proof} onChange={(e) => setProof(e.target.value)}
                placeholder="LinkedIn / company page / verification doc URL" className="mt-2 w-full border border-black bg-transparent px-3 py-3 text-sm outline-none" />
            </div>
            <div>
              <label className="label-overline">Anything else?</label>
              <textarea data-testid="supplier-claim-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={3}
                placeholder="Tell us about your relationship to this factory."
                className="mt-2 w-full border border-black bg-transparent px-3 py-3 text-sm outline-none" />
            </div>
            <button data-testid="supplier-claim-submit" disabled={busy} className="inline-flex items-center gap-2 bg-black px-5 py-3 text-xs font-medium uppercase tracking-wider text-white hover:bg-neutral-800 disabled:opacity-50">
              {busy ? <Loader2 size={14} strokeWidth={1.5} className="animate-spin" /> : <Check size={14} strokeWidth={1.5} />} File claim
            </button>
          </form>
        </div>
      </section>
    </div>
  );
};

// ── 2. Dashboard ──────────────────────────────────────────────────────────
export const SupplierDashboard = () => {
  const [profile, setProfile] = useState(null);
  const [metrics, setMetrics] = useState({});
  const [rfqs, setRfqs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([supplierClient.myProfile(), supplierClient.metrics(), supplierClient.myRfqs()])
      .then(([p, m, r]) => { setProfile(p); setMetrics(m); setRfqs(r.slice(0, 5)); })
      .catch((e) => toast.error(e?.message || 'Could not load dashboard.'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex min-h-[60vh] items-center justify-center"><Loader2 size={20} strokeWidth={1.5} className="animate-spin text-neutral-500" /></div>;

  return (
    <div data-testid="supplier-dashboard" className="bg-[#F5F5F0]">
      <SEO title="Supplier dashboard" noindex />
      <section className="border-b border-black bg-white">
        <div className="mx-auto max-w-[1440px] px-5 py-10 md:px-8 md:py-12">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <span className="label-overline text-neutral-500">/ Supplier · Dashboard</span>
              <h1 className="mt-3 font-display tracking-tighter" style={{ fontWeight: 700, fontSize: 'clamp(2rem,4vw,3.25rem)', lineHeight: 0.95 }}>{profile?.name}.</h1>
              <div className="mt-4 flex flex-wrap items-center gap-3">
                <TierBadge tier={profile?.tier || 'verified'} />
                <span className="font-mono-data text-[10px] uppercase tracking-wider text-neutral-500">{TIER_META[profile?.tier]?.tone}</span>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Link to="/supplier/profile" data-testid="supplier-nav-profile" className="btn-giwahs btn-giwahs-secondary">Edit profile</Link>
              <Link to="/supplier/inbox" data-testid="supplier-nav-inbox" className="btn-giwahs btn-giwahs-primary">RFQ inbox →</Link>
            </div>
          </div>
        </div>
      </section>

      <section className="border-b border-black">
        <div className="mx-auto max-w-[1440px] grid grid-cols-2 gap-3 px-5 py-6 md:grid-cols-4 md:px-8">
          <Metric label="RFQs assigned"   value={metrics.rfqs_total ?? 0} />
          <Metric label="Quotes submitted" value={metrics.rfqs_quoted ?? 0} />
          <Metric label="Open inbox"      value={metrics.rfqs_open ?? 0} sublabel="needs quote" />
          <Metric label="Response rate"   value={`${metrics.response_rate_pct ?? 0}%`} sublabel="all-time" />
        </div>
      </section>

      <section className="bg-[#F5F5F0]">
        <div className="mx-auto max-w-[1440px] px-5 py-8 md:px-8 md:py-10">
          <div className="flex items-end justify-between">
            <span className="label-overline">Recent RFQs</span>
            <Link to="/supplier/inbox" className="text-xs uppercase tracking-wider hover:underline">View all →</Link>
          </div>
          <div className="mt-4 space-y-2">
            {rfqs.length === 0
              ? <div className="border border-dashed border-neutral-300 bg-white/40 p-6 text-center text-sm text-neutral-500">No RFQs assigned yet. The GIWAHS sourcing desk will route matching buyer briefs to your inbox.</div>
              : rfqs.map((r) => <SupplierRfqRow key={r.reference_code} rfq={r} />)
            }
          </div>
        </div>
      </section>
    </div>
  );
};

const SupplierRfqRow = ({ rfq }) => {
  const cat = (rfq.product_type || '').split(':')[0]?.trim() || '—';
  const qty = rfq.target_quantity ? `${Number(rfq.target_quantity).toLocaleString()} units` : '— units';
  const quoted = rfq.my_quote_status;
  return (
    <Link to={`/supplier/inbox/${rfq.reference_code}`} data-testid={`supplier-rfq-row-${rfq.reference_code}`}
      className="flex items-center justify-between gap-3 border border-black bg-white px-4 py-3 hover:bg-neutral-50">
      <div className="flex flex-1 flex-wrap items-baseline gap-3 truncate">
        <span className="font-mono-data text-[11px] tracking-wider">{rfq.reference_code}</span>
        <span className="text-sm font-semibold">{cat}</span>
        <span className="text-xs text-neutral-600">{qty}</span>
        <span className="text-xs text-neutral-500">{rfq.shipping_destination || '—'}</span>
      </div>
      <div className="flex items-center gap-2">
        {quoted
          ? <span className="border border-emerald-600 bg-emerald-50 px-2 py-0.5 font-mono-data text-[10px] uppercase tracking-wider text-emerald-900">Quoted</span>
          : <span className="border border-amber-500 bg-amber-50 px-2 py-0.5 font-mono-data text-[10px] uppercase tracking-wider text-amber-900">Awaiting quote</span>}
        <ArrowRight size={14} strokeWidth={1.5} />
      </div>
    </Link>
  );
};

// ── 3. Inbox ──────────────────────────────────────────────────────────────
export const SupplierInbox = () => {
  const [rfqs, setRfqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // 'all' | 'open' | 'quoted'

  useEffect(() => {
    track(EVENTS.SUPPLIER_INBOX_VIEWED, {});
    supplierClient.myRfqs().then(setRfqs).finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => rfqs.filter(r => {
    if (filter === 'open')   return !r.my_quote_id;
    if (filter === 'quoted') return !!r.my_quote_id;
    return true;
  }), [rfqs, filter]);

  return (
    <div data-testid="supplier-inbox-page" className="bg-[#F5F5F0]">
      <SEO title="RFQ Inbox — Supplier" noindex />
      <section className="border-b border-black bg-white">
        <div className="mx-auto max-w-[1440px] px-5 py-8 md:px-8 md:py-10">
          <Link to="/supplier/dashboard" className="inline-flex items-center gap-1 text-xs uppercase tracking-wider text-neutral-500 hover:text-black"><ChevronLeft size={12} strokeWidth={1.5} /> Dashboard</Link>
          <h1 className="mt-2 font-display tracking-tighter" style={{ fontWeight: 700, fontSize: 'clamp(1.75rem,3.5vw,2.75rem)', lineHeight: 0.95 }}>RFQ inbox.</h1>
          <div className="mt-4 flex gap-2">
            {[['all','All'],['open','Needs quote'],['quoted','Quoted']].map(([k,l]) => (
              <button key={k} onClick={() => setFilter(k)} data-testid={`supplier-inbox-filter-${k}`}
                className={`border px-3 py-1.5 font-mono-data text-[10px] uppercase tracking-wider ${filter===k?'border-black bg-black text-white':'border-black hover:bg-black hover:text-white'}`}>{l}</button>
            ))}
          </div>
        </div>
      </section>

      <section>
        <div className="mx-auto max-w-[1440px] px-5 py-6 md:px-8">
          {loading ? <Loader2 size={20} strokeWidth={1.5} className="animate-spin text-neutral-500 mx-auto my-12" /> :
            filtered.length === 0
              ? <div className="border border-dashed border-neutral-300 bg-white p-10 text-center text-sm text-neutral-500" data-testid="supplier-inbox-empty">No RFQs match this filter.</div>
              : <div className="space-y-2">{filtered.map((r) => <SupplierRfqRow key={r.reference_code} rfq={r} />)}</div>
          }
        </div>
      </section>
    </div>
  );
};

// ── 4. RFQ detail + quote submit ──────────────────────────────────────────
export const SupplierRfqDetail = () => {
  const { reference_code } = useParams();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ unit_price: '', moq: '', lead_time_days: '', notes: '', attachments_url: '' });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const d = await supplierClient.getRfq(reference_code);
      setDetail(d);
      if (d?.my_quote) {
        setForm({
          unit_price: d.my_quote.unit_price || '',
          moq:        d.my_quote.moq || '',
          lead_time_days: d.my_quote.lead_time_days || '',
          notes:      d.my_quote.notes || '',
          attachments_url: d.my_quote.attachments_url || '',
        });
      }
      track(EVENTS.SUPPLIER_RFQ_OPENED, { reference_code });
    } finally { setLoading(false); }
  }, [reference_code]);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (busy || !form.unit_price.trim()) return;
    setBusy(true);
    try {
      await supplierClient.submitQuote(reference_code, {
        unit_price: form.unit_price,
        moq:            form.moq ? Number(form.moq) : null,
        lead_time_days: form.lead_time_days ? Number(form.lead_time_days) : null,
        notes:          form.notes || null,
        attachments_url:form.attachments_url || null,
      });
      toast.success('Quote submitted to the GIWAHS sourcing desk.');
      await load();
    } catch (err) {
      toast.error(err?.message || 'Could not submit quote.');
    } finally { setBusy(false); }
  };

  if (loading) return <div className="flex min-h-[60vh] items-center justify-center"><Loader2 size={20} strokeWidth={1.5} className="animate-spin text-neutral-500" /></div>;
  if (!detail) return (
    <div className="mx-auto max-w-2xl py-24 text-center"><AlertCircle size={20} strokeWidth={1.5} className="mx-auto text-neutral-500" /><p className="mt-4 text-neutral-700">This RFQ is not visible to your account.</p><Link to="/supplier/inbox" className="mt-6 inline-block btn-giwahs btn-giwahs-secondary">Back to inbox</Link></div>
  );

  const has = !!detail.my_quote;

  return (
    <div data-testid="supplier-rfq-detail" className="bg-white">
      <SEO title={`RFQ ${reference_code} — Supplier`} noindex />
      <section className="border-b border-black">
        <div className="mx-auto max-w-3xl px-5 py-10 md:py-14">
          <Link to="/supplier/inbox" className="inline-flex items-center gap-1 text-xs uppercase tracking-wider text-neutral-500 hover:text-black"><ChevronLeft size={12} strokeWidth={1.5} /> Inbox</Link>
          <h1 className="mt-3 font-display tracking-tighter" style={{ fontWeight: 700, fontSize: 'clamp(1.75rem,3.5vw,2.75rem)', lineHeight: 0.95 }}>{detail.reference_code}</h1>

          <div className="mt-6 grid gap-2 border border-black p-5 text-sm">
            <div><span className="label-overline text-neutral-500">Product</span><div>{detail.product_type || '—'}</div></div>
            <div className="grid grid-cols-2 gap-2">
              <div><span className="label-overline text-neutral-500">Quantity</span><div>{detail.target_quantity ? `${Number(detail.target_quantity).toLocaleString()} units` : '—'}</div></div>
              <div><span className="label-overline text-neutral-500">Target price</span><div>{detail.target_unit_price || '—'}</div></div>
            </div>
            <div><span className="label-overline text-neutral-500">Destination</span><div>{detail.shipping_destination || '—'}</div></div>
            {detail.needed_by_date && <div><span className="label-overline text-neutral-500">Needed by</span><div>{detail.needed_by_date}</div></div>}
            {detail.notes && <div><span className="label-overline text-neutral-500">Buyer notes</span><div className="whitespace-pre-wrap">{detail.notes}</div></div>}
          </div>

          <form onSubmit={submit} data-testid="supplier-quote-form" className="mt-8 space-y-4 border border-black p-5">
            <div className="flex items-center justify-between">
              <span className="label-overline">{has ? 'Revise your quote' : 'Submit your quote'}</span>
              {has && <span className="border border-emerald-600 bg-emerald-50 px-2 py-0.5 font-mono-data text-[10px] uppercase tracking-wider text-emerald-900">{detail.my_quote.status}</span>}
            </div>
            <div>
              <label className="label-overline text-neutral-500">Unit price *</label>
              <input data-testid="supplier-quote-price" required value={form.unit_price} onChange={(e) => setForm({...form, unit_price: e.target.value})}
                placeholder="$18.50 / unit FOB Shenzhen" className="mt-1 w-full border border-black bg-transparent px-3 py-2 text-sm outline-none" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label-overline text-neutral-500">MOQ</label>
                <input data-testid="supplier-quote-moq" type="number" min="1" value={form.moq} onChange={(e) => setForm({...form, moq: e.target.value})}
                  className="mt-1 w-full border border-black bg-transparent px-3 py-2 text-sm outline-none" />
              </div>
              <div>
                <label className="label-overline text-neutral-500">Lead time (days)</label>
                <input data-testid="supplier-quote-lead" type="number" min="1" value={form.lead_time_days} onChange={(e) => setForm({...form, lead_time_days: e.target.value})}
                  className="mt-1 w-full border border-black bg-transparent px-3 py-2 text-sm outline-none" />
              </div>
            </div>
            <div>
              <label className="label-overline text-neutral-500">Notes for the sourcing desk</label>
              <textarea data-testid="supplier-quote-notes" rows={3} value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})}
                placeholder="Production capacity, customisation options, samples available, etc." className="mt-1 w-full border border-black bg-transparent px-3 py-2 text-sm outline-none" />
            </div>
            <div>
              <label className="label-overline text-neutral-500">Attachment URL (optional)</label>
              <input data-testid="supplier-quote-attachment" value={form.attachments_url} onChange={(e) => setForm({...form, attachments_url: e.target.value})}
                placeholder="Tech pack, spec sheet, sample image (link)" className="mt-1 w-full border border-black bg-transparent px-3 py-2 text-sm outline-none" />
            </div>
            <button data-testid="supplier-quote-submit" disabled={busy || !form.unit_price.trim()} className="inline-flex items-center gap-2 bg-black px-5 py-3 text-xs font-medium uppercase tracking-wider text-white hover:bg-neutral-800 disabled:opacity-50">
              {busy ? <Loader2 size={14} strokeWidth={1.5} className="animate-spin" /> : <Check size={14} strokeWidth={1.5} />} {has ? 'Update quote' : 'Submit quote'}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
};

// ── 5. Profile editor ─────────────────────────────────────────────────────
export const SupplierMyProfile = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => { supplierClient.myProfile().then((p) => { setProfile(p); setLoading(false); }); }, []);

  const handleChange = (k, v) => setProfile({ ...profile, [k]: v });

  const save = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await supplierClient.updateProfile({
        tagline: profile.tagline, city: profile.city,
        founded_year: profile.founded_year, employees_range: profile.employees_range,
        moq_min: profile.moq_min, moq_max: profile.moq_max,
        lead_time_days: profile.lead_time_days,
        production_capacity_monthly: profile.production_capacity_monthly,
        pricing_indicator: profile.pricing_indicator,
        hero_image: profile.hero_image,
        categories:  Array.isArray(profile.categories) ? profile.categories : (profile.categories||'').split(',').map(s=>s.trim()).filter(Boolean),
        specialties: Array.isArray(profile.specialties)? profile.specialties: (profile.specialties||'').split(',').map(s=>s.trim()).filter(Boolean),
      });
      toast.success('Profile updated.');
    } catch (err) { toast.error(err?.message || 'Could not save.'); }
    finally { setBusy(false); }
  };

  if (loading || !profile) return <div className="flex min-h-[60vh] items-center justify-center"><Loader2 size={20} strokeWidth={1.5} className="animate-spin text-neutral-500" /></div>;

  return (
    <div data-testid="supplier-profile-page" className="bg-white">
      <SEO title="Edit profile — Supplier" noindex />
      <section className="border-b border-black">
        <div className="mx-auto max-w-3xl px-5 py-10 md:py-14">
          <Link to="/supplier/dashboard" className="inline-flex items-center gap-1 text-xs uppercase tracking-wider text-neutral-500 hover:text-black"><ChevronLeft size={12} strokeWidth={1.5} /> Dashboard</Link>
          <h1 className="mt-3 font-display tracking-tighter" style={{ fontWeight: 700, fontSize: 'clamp(1.75rem,3.5vw,2.75rem)', lineHeight: 0.95 }}>Edit profile.</h1>
          <p className="mt-2 text-sm text-neutral-600">Updates are live on giwahs.com immediately. Trust score, certifications, and brand associations are managed by GIWAHS.</p>

          <div className="mt-6 flex items-center gap-3">
            <TierBadge tier={profile.tier} />
            <Link to={`/factory/${profile.slug}`} target="_blank" className="inline-flex items-center gap-1.5 border border-black px-3 py-1.5 text-[10px] uppercase tracking-wider hover:bg-black hover:text-white">Public listing <ExternalLink size={10} strokeWidth={1.5} /></Link>
          </div>

          <form onSubmit={save} className="mt-8 space-y-5">
            <Field label="Tagline"      testid="sup-profile-tagline" value={profile.tagline} onChange={(v) => handleChange('tagline', v)} />
            <Field label="City"         testid="sup-profile-city"    value={profile.city}    onChange={(v) => handleChange('city', v)} />
            <div className="grid grid-cols-2 gap-3">
              <Field label="Founded year"     testid="sup-profile-founded"   value={profile.founded_year} onChange={(v) => handleChange('founded_year', v)} type="number" />
              <Field label="Employees range"  testid="sup-profile-employees" value={profile.employees_range} onChange={(v) => handleChange('employees_range', v)} placeholder="e.g. 50-200" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="MOQ min"  testid="sup-profile-moq-min" type="number" value={profile.moq_min} onChange={(v) => handleChange('moq_min', v)} />
              <Field label="MOQ max"  testid="sup-profile-moq-max" type="number" value={profile.moq_max} onChange={(v) => handleChange('moq_max', v)} />
            </div>
            <Field label="Lead time"          testid="sup-profile-leadtime" value={profile.lead_time_days} onChange={(v) => handleChange('lead_time_days', v)} placeholder="e.g. 45-60 days" />
            <Field label="Monthly capacity"   testid="sup-profile-capacity" value={profile.production_capacity_monthly} onChange={(v) => handleChange('production_capacity_monthly', v)} placeholder="e.g. 25,000 units" />
            <Field label="Pricing indicator"  testid="sup-profile-pricing"  value={profile.pricing_indicator} onChange={(v) => handleChange('pricing_indicator', v)} placeholder="$, $$, $$$, $$$$" />
            <Field label="Hero image URL"     testid="sup-profile-hero"     value={profile.hero_image} onChange={(v) => handleChange('hero_image', v)} placeholder="https://..." />
            <Field label="Categories (comma-separated)"  testid="sup-profile-categories"  value={Array.isArray(profile.categories) ? profile.categories.join(', ') : profile.categories} onChange={(v) => handleChange('categories', v)} />
            <Field label="Specialties (comma-separated)" testid="sup-profile-specialties" value={Array.isArray(profile.specialties) ? profile.specialties.join(', ') : profile.specialties} onChange={(v) => handleChange('specialties', v)} />

            <button data-testid="sup-profile-save" disabled={busy} className="inline-flex items-center gap-2 bg-black px-5 py-3 text-xs font-medium uppercase tracking-wider text-white hover:bg-neutral-800 disabled:opacity-50">
              {busy ? <Loader2 size={14} strokeWidth={1.5} className="animate-spin" /> : <Check size={14} strokeWidth={1.5} />} Save changes
            </button>
          </form>
        </div>
      </section>
    </div>
  );
};

const Field = ({ label, value, onChange, type = 'text', placeholder, testid }) => (
  <div>
    <label className="label-overline">{label}</label>
    <input data-testid={testid} type={type} value={value || ''} onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="mt-1 w-full border border-black bg-transparent px-3 py-2 text-sm outline-none" />
  </div>
);
